import { useEffect, useRef } from 'react';
import { useDispatch } from "react-redux";
import { DocumentActionTypes } from "../../../Actions/actionTypes";
import { detectVersionParties, getVersionStatus } from "../../../Services/documents.service";

export const useVersionStatusPolling = (versionId, { onMetadataRequired } = {}) => {
    const dispatch = useDispatch();
    const timerRef = useRef(null);
    const requestedPartyDetectionRef = useRef(new Set());
    const requestedMetadataRef = useRef(new Set());

    useEffect(() => {
        if (!versionId) return;

        let cancelled = false;

        const tick = async () => {
            if (cancelled) return;

            try {
                const res = await getVersionStatus(versionId);

                const data = res?.data || {};

                const extractionStatus = data?.extraction_status;
                let partyDetectionStatus = data?.party_detection_status || "not_started";
                const statusPayload = {
                    extraction_status: extractionStatus,
                    ocr_status: data?.ocr_status,
                    ocr_user_message: data?.ocr_user_message,
                    analysis_allowed: data?.analysis_allowed,
                    party_detection_status: partyDetectionStatus,
                    party_detection_attempts: data?.party_detection_attempts || 0,
                    party_detection_error: data?.party_detection_error || null,
                    party_detection_json: data?.party_detection_json || null,
                };

                dispatch({
                    type: DocumentActionTypes.SET_VERSION_STATUS,
                    payload: statusPayload,
                });

                if (extractionStatus === "processing") {
                    timerRef.current = setTimeout(tick, 3000);
                    return;
                }

                if (
                    extractionStatus === "succeeded" &&
                    ["not_started", "failed_retryable"].includes(partyDetectionStatus)
                ) {
                    if (!requestedPartyDetectionRef.current.has(versionId)) {
                        requestedPartyDetectionRef.current.add(versionId);
                        try {
                            const detectionResponse = await detectVersionParties(versionId);
                            const detectionData = detectionResponse?.data || {};
                            partyDetectionStatus = detectionData?.party_detection_status || partyDetectionStatus;
                            dispatch({
                                type: DocumentActionTypes.SET_VERSION_STATUS,
                                payload: {
                                    party_detection_status: partyDetectionStatus,
                                    party_detection_attempts: detectionData?.party_detection_attempts || 0,
                                    party_detection_error: detectionData?.party_detection_error || null,
                                    party_detection_json: detectionData?.party_detection_json || null,
                                },
                            });
                        } finally {
                            requestedPartyDetectionRef.current.delete(versionId);
                        }
                    }
                    timerRef.current = setTimeout(tick, 1000);
                    return;
                }

                if (partyDetectionStatus === "processing") {
                    timerRef.current = setTimeout(tick, 1500);
                    return;
                }

                if (
                    extractionStatus === 'succeeded'
                    && onMetadataRequired
                    && !requestedMetadataRef.current.has(versionId)
                ) {
                    requestedMetadataRef.current.add(versionId);
                    onMetadataRequired(versionId);
                }
            } catch (e) {
                console.error("POLL ERROR", e);
                timerRef.current = setTimeout(tick, 5000);
            }
        };

        tick();

        return () => {
            cancelled = true;
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [dispatch, versionId, onMetadataRequired]);

};
